/**
 * Shared URL → preview helpers for every post composer (CreatePostModal,
 * UserTimeline, PitPostsTab, etc). Two flavors:
 *
 *   1. Market / event previews (this is the original use case)
 *        - polymarket.com/event/<slug>
 *        - polymarket.com/market/<slug>
 *        - kalshi.com/markets/<ticker> | /events/<event>
 *        - predict.fun/markets/<slug>  | /category/<slug>
 *        - frenflow.com/event/<slug>          (assume polymarket source)
 *        - frenflow.com/market/<slug>         (assume polymarket source)
 *        - frenflow.com/polymarket/event/<slug>
 *        - frenflow.com/polymarket/market/<slug>
 *        - frenflow.com/kalshi/event/<ticker>
 *        - frenflow.com/predictfun/event/<slug>
 *        - frenflow.com/predictfun/market/<slug>
 *
 *   2. Trader / wallet previews
 *        - frenflow.com/profile/<username>
 *        - frenflow.com/traders/<address>
 *        - polymarket.com/profile/<address>
 *        - bare wallet address pasted in the text (0x[a-f0-9]{40})
 *
 * Each kind resolves through its own backend endpoint and returns a
 * shape tagged with `kind: 'market' | 'trader'`. Composers persist that
 * tag in `posts.link_preview.kind` so the post-render layer knows which
 * card to draw.
 */

export type MarketPreview = {
  id: string;
  question?: string;
  title?: string;
  description?: string;
  image?: string;
  outcomes?: string[];
  outcomePrices?: string[];
  volume?: number;
  liquidity?: number;
  endDate?: string | null;
  active?: boolean;
  closed?: boolean;
  url: string;
  source?: string;
  slug?: string;
  isEvent?: boolean;
  markets?: Array<{
    slug?: string;
    question?: string;
    outcomePrices?: string[];
    volume?: number;
  }>;
  marketsCount?: number;
};

export type MarketSource = "polymarket" | "kalshi" | "predict";

export const ALLOWED_DOMAINS = [
  "polymarket.com",
  "frenflow.com",
  "polyhub.com",
  "kalshi.com",
  "predict.fun",
] as const;

export function extractUrls(text: string): string[] {
  const re = /https?:\/\/[^\s<]+/g;
  return text.match(re) || [];
}

export function isAllowedUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return ALLOWED_DOMAINS.some((d) => u.hostname === d || u.hostname.endsWith(`.${d}`));
  } catch {
    return false;
  }
}

/**
 * Extract slug + source from any supported URL. Returns null if the URL
 * does not point to a market or event we can preview.
 */
export function extractMarketSlug(
  url: string,
): { slug: string; source: MarketSource; kind: "event" | "market" } | null {
  try {
    const u = new URL(url);
    const path = u.pathname;
    // ?market=<slug> on an event URL means the user is sharing a SPECIFIC
    // sub-market — surface that as the preview, not the parent event.
    const marketParam = u.searchParams.get("market");

    if (u.hostname.includes("kalshi.com")) {
      const m = path.match(/\/(markets|events)\/([^/?]+)/);
      if (m) return { slug: m[2], source: "kalshi", kind: m[1] === "events" ? "event" : "market" };
    }
    if (u.hostname.includes("predict.fun")) {
      const m = path.match(/\/(markets|category)\/([^/?]+)/);
      if (m) return { slug: m[2], source: "predict", kind: "market" };
    }
    if (u.hostname.includes("polymarket.com")) {
      const m = path.match(/\/(event|market)\/([^/?]+)/);
      if (m) {
        // Event URL with ?market=<slug> → return the sub-market.
        if (m[1] === "event" && marketParam) {
          return { slug: marketParam, source: "polymarket", kind: "market" };
        }
        return { slug: m[2], source: "polymarket", kind: m[1] as "event" | "market" };
      }
    }
    if (u.hostname.includes("frenflow.com") || u.hostname.includes("localhost")) {
      // /polymarket|kalshi|predictfun/event|market/slug
      const scoped = path.match(/^\/(polymarket|kalshi|predictfun)\/(event|market)\/([^/?]+)/);
      if (scoped) {
        const src = scoped[1] === "predictfun" ? "predict" : (scoped[1] as MarketSource);
        // Event URL with ?market=<slug> on polymarket → sub-market preview
        if (scoped[2] === "event" && src === "polymarket" && marketParam) {
          return { slug: marketParam, source: "polymarket", kind: "market" };
        }
        return { slug: scoped[3], source: src, kind: scoped[2] as "event" | "market" };
      }
      // Bare /event|market/slug — assume Polymarket (canonical FrenFlow URL)
      const bare = path.match(/^\/(event|market)\/([^/?]+)/);
      if (bare) {
        if (bare[1] === "event" && marketParam) {
          return { slug: marketParam, source: "polymarket", kind: "market" };
        }
        return { slug: bare[2], source: "polymarket", kind: bare[1] as "event" | "market" };
      }
    }
    return null;
  } catch {
    return null;
  }
}

export function isMarketUrl(url: string): boolean {
  return extractMarketSlug(url) !== null;
}

/**
 * Fetch a normalized MarketPreview for a supported URL. Dispatches to
 * the right backend endpoint based on source. Returns null if the URL
 * is unsupported or the lookup fails.
 *
 * Pass an AbortSignal to cancel in-flight previews when the user keeps
 * typing (avoids stale state writes).
 */
export async function fetchMarketPreview(
  url: string,
  opts: { signal?: AbortSignal } = {},
): Promise<MarketPreview | null> {
  const parsed = extractMarketSlug(url);
  if (!parsed) return null;

  try {
    if (parsed.source === "kalshi") {
      const res = await fetch(`/api/markets/kalshi/${encodeURIComponent(parsed.slug)}`, {
        signal: opts.signal,
      });
      if (!res.ok) return null;
      const d = await res.json();
      const m = d.market || d;
      if (!m) return null;
      return {
        id: m.ticker || parsed.slug,
        slug: parsed.slug,
        title: m.title || m.question || parsed.slug,
        question: m.title || m.question,
        image: m.image || m.imageUrl,
        outcomes: ["Yes", "No"],
        outcomePrices: [String(m.yesPrice ?? 0.5), String(m.noPrice ?? 0.5)],
        volume: m.volume,
        url,
        source: "kalshi",
      };
    }

    if (parsed.source === "predict") {
      const res = await fetch(`/api/markets/predict/${encodeURIComponent(parsed.slug)}`, {
        signal: opts.signal,
      });
      if (!res.ok) return null;
      const d = await res.json();
      const m = d.market || d;
      if (!m) return null;
      const chance = m.markets?.[0]?.chancePercentage ?? 50;
      return {
        id: m.id || parsed.slug,
        slug: parsed.slug,
        title: m.title || m.question || parsed.slug,
        question: m.title || m.question,
        image: m.imageUrl || m.image,
        outcomes: m.outcomes?.map((o: { name?: string } | string) =>
          typeof o === "string" ? o : o.name ?? "",
        ) || ["Yes", "No"],
        outcomePrices: [String(chance / 100), String(1 - chance / 100)],
        volume: m.volume,
        url,
        source: "predict",
      };
    }

    // Polymarket — the same proxy resolves both events and markets, but
    // it discriminates by inspecting the URL path: `?slug=` is always
    // treated as event, while `?url=` checks for `/event/` vs `/market/`.
    // Pass the canonical polymarket URL of the right kind so a sub-market
    // shared via `?market=` doesn't fall back to its parent event card.
    const polyUrl =
      parsed.kind === "market"
        ? `https://polymarket.com/market/${encodeURIComponent(parsed.slug)}`
        : `https://polymarket.com/event/${encodeURIComponent(parsed.slug)}`;
    const res = await fetch(
      `/api/polymarket/event?url=${encodeURIComponent(polyUrl)}`,
      { signal: opts.signal },
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.success || !data.data) return null;
    return { ...data.data, url };
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") return null;
    return null;
  }
}

export const ALLOWED_DOMAINS_HUMAN =
  "polymarket.com / frenflow.com / kalshi.com / predict.fun";

// =============================================================================
// TRADER / WALLET PREVIEW
// =============================================================================

export type TraderPreview = {
  kind: "trader";
  address: string | null;
  isFrenFlowUser: boolean;
  user: {
    id: string;
    username: string | null;
    name: string | null;
    image: string | null;
    bio: string | null;
  } | null;
  stats: {
    volume: number;
    pnl: number;
    realizedPnl: number;
    winRate: number;
    trades: number;
    marketsTraded: number;
    primaryStyle: string | null;
    activePositions: number;
    lastTrade: string | null;
  } | null;
  copiers: number;
  /** Original pasted URL/wallet (kept so we can rebuild the link in posts). */
  url: string;
};

/** Discriminated preview union persisted in posts.link_preview. */
export type LinkPreview =
  | ({ kind: "market" } & MarketPreview)
  | TraderPreview;

const HEX40 = /^0x[a-f0-9]{40}$/i;

/**
 * Recognise FrenFlow / Polymarket profile links and bare EVM addresses.
 * Returns the best ref for `/api/social/trader-preview?ref=...` along
 * with the original matched substring (so callers can dedupe vs the
 * marketPreview state — same URL means same preview).
 */
export function extractTraderRef(
  text: string,
): { ref: string; matched: string; kind: "address" | "username" } | null {
  // Bare wallet address — anywhere in the text.
  const addrMatch = text.match(/0x[a-fA-F0-9]{40}/);
  if (addrMatch) {
    return { ref: addrMatch[0].toLowerCase(), matched: addrMatch[0], kind: "address" };
  }

  // URLs in the text → try to map each one to a trader ref.
  const urls = extractUrls(text);
  for (const raw of urls) {
    try {
      const u = new URL(raw);
      // FrenFlow profile by username
      if (u.hostname.includes("frenflow.com") || u.hostname.includes("localhost")) {
        const m = u.pathname.match(/^\/profile\/([^/?]+)/);
        if (m) {
          const ref = decodeURIComponent(m[1]).replace(/^@/, "");
          if (/^[a-zA-Z0-9_-]{1,40}$/.test(ref)) {
            return { ref, matched: raw, kind: "username" };
          }
        }
        // FrenFlow trader page — accepts both addresses and usernames
        // (with optional @ prefix). /traders/<X> serves both, we detect
        // by shape so users can paste either form.
        const t = u.pathname.match(/^\/traders\/([^/?]+)/);
        if (t) {
          const seg = decodeURIComponent(t[1]).replace(/^@/, "");
          if (/^0x[a-fA-F0-9]{40}$/.test(seg)) {
            return { ref: seg.toLowerCase(), matched: raw, kind: "address" };
          }
          if (/^[a-zA-Z0-9_-]{1,40}$/.test(seg)) {
            return { ref: seg, matched: raw, kind: "username" };
          }
        }
      }
      // Polymarket profile by address
      if (u.hostname.includes("polymarket.com")) {
        const m = u.pathname.match(/^\/profile\/(0x[a-fA-F0-9]{40})/);
        if (m) return { ref: m[1].toLowerCase(), matched: raw, kind: "address" };
      }
    } catch {
      /* skip malformed */
    }
  }
  return null;
}

/**
 * Fetch a trader preview by address or username via the social proxy.
 * Returns null on any failure so the composer just shows nothing rather
 * than blocking the post.
 */
export async function fetchTraderPreview(
  ref: { ref: string; matched: string; kind: "address" | "username" },
  opts: { signal?: AbortSignal } = {},
): Promise<TraderPreview | null> {
  try {
    const qs = new URLSearchParams();
    qs.set(ref.kind === "address" ? "address" : "username", ref.ref);
    const res = await fetch(`/api/social/trader-preview?${qs.toString()}`, {
      signal: opts.signal,
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.trader) return null;
    return {
      kind: "trader",
      ...data.trader,
      url: ref.matched,
    } as TraderPreview;
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") return null;
    return null;
  }
}

/**
 * Single dispatch — given the textarea content, find whichever entity
 * is referenced (market beats trader if both appear, since trader refs
 * are often substrings of market URLs in pasted content). Returns the
 * resolved preview tagged with `kind`, or null.
 *
 * Composers should call this once per content change and let the
 * returned shape decide which card to render. AbortSignal lets callers
 * cancel an in-flight resolve when the user keeps typing.
 */
export async function resolvePostLinkPreview(
  text: string,
  opts: { signal?: AbortSignal } = {},
): Promise<LinkPreview | null> {
  // Markets first — denser signal (slug + path tells us a lot).
  const urls = extractUrls(text);
  const marketUrl = urls.find((u) => isMarketUrl(u));
  if (marketUrl) {
    const m = await fetchMarketPreview(marketUrl, opts);
    if (m) return { kind: "market", ...m };
  }

  // Then traders.
  const t = extractTraderRef(text);
  if (t) return await fetchTraderPreview(t, opts);

  return null;
}
